import { useState } from 'react';
import { useApp } from '../context/AppContext';

// ── Custom confirm dialog ─────────────────────────────────────────────────
function ConfirmDialog({ message, onConfirm, onCancel }) {
  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,.45)',
      zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center',
    }}>
      <div style={{
        background: 'var(--sur)', border: '1px solid var(--bd)', borderRadius: 'var(--rl)',
        boxShadow: '0 20px 60px rgba(0,0,0,.25)', padding: '28px 32px', maxWidth: 380, width: '90%',
        textAlign: 'center',
      }}>
        <div style={{ fontSize: 28, marginBottom: 12 }}>💾</div>
        <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 6 }}>Confirm</div>
        <div style={{ fontSize: 12, color: 'var(--tx2)', marginBottom: 24, lineHeight: 1.5 }}>{message}</div>
        <div style={{ display: 'flex', gap: 10, justifyContent: 'center' }}>
          <button className="btn btn-ghost" onClick={onCancel} style={{ minWidth: 90 }}>Cancel</button>
          <button className="btn btn-primary" onClick={onConfirm} style={{ minWidth: 90 }}>Confirm</button>
        </div>
      </div>
    </div>
  );
}

// ── Defined OUTSIDE component to prevent remount on every render ──────────

function Field({ label, children }) {
  return (
    <div className="fg" style={{ display: 'grid', gridTemplateColumns: '160px 1fr', alignItems: 'center', gap: 10 }}>
      <label className="fl" style={{ fontSize: 11, color: 'var(--tx2)', fontWeight: 500 }}>{label}</label>
      {children}
    </div>
  );
}

function SubHeader({ label }) {
  return (
    <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--tx3)', textTransform: 'uppercase', letterSpacing: 1, marginTop: 6, marginBottom: 4, paddingTop: 14, borderTop: '1px solid var(--bd)' }}>
      {label}
    </div>
  );
}

function CollapSection({ isOpen, onToggle, title, isEditing, onEdit, onSave, onCancel, children }) {
  return (
    <div style={{ background: 'var(--sur)', border: `1px solid ${isEditing ? 'var(--ac)' : 'var(--bd)'}`, borderRadius: 'var(--rl)', boxShadow: 'var(--sh)', marginBottom: 14, overflow: 'hidden', transition: 'border-color .15s' }}>
      <div style={{ padding: '12px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', userSelect: 'none' }}>
        {/* Title — clicks toggle open/close */}
        <div onClick={onToggle} style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8, flex: 1 }}>
          <span style={{ fontSize: 12, fontWeight: 600 }}>{title}</span>
          <span style={{ fontSize: 13, color: 'var(--tx3)', transition: 'transform .2s', display: 'inline-block', transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)' }}>▼</span>
        </div>
        {/* Action buttons */}
        {isOpen && (
          isEditing ? (
            <div style={{ display: 'flex', gap: 6 }}>
              <button className="btn btn-primary btn-sm" onClick={e => { e.stopPropagation(); onSave(); }}>💾 Save</button>
              <button className="btn btn-ghost btn-sm" onClick={e => { e.stopPropagation(); onCancel(); }}>✕ Cancel</button>
            </div>
          ) : (
            <button className="btn btn-ghost btn-sm" onClick={e => { e.stopPropagation(); onEdit(); }}>✏️ Edit</button>
          )
        )}
      </div>
      {isOpen && (
        <div style={{ borderTop: '1px solid var(--bd)', padding: 16, display: 'flex', flexDirection: 'column', gap: 10 }}>
          {children}
        </div>
      )}
    </div>
  );
}

// ── Read-only display value ───────────────────────────────────────────────
function DisplayVal({ value, placeholder = '—' }) {
  return (
    <span style={{ fontSize: 12, color: value ? 'var(--tx)' : 'var(--tx3)', fontStyle: value ? 'normal' : 'italic' }}>
      {value || placeholder}
    </span>
  );
}

// ─────────────────────────────────────────────────────────────────────────

export default function SettingsPage() {
  const { companySettings, setCompanySettings, showToast, resetDemoData } = useApp();

  const [s, setS] = useState(companySettings);
  const [dialog, setDialog] = useState(null); // { message, onConfirm }

  function ask(message, onConfirm) {
    setDialog({ message, onConfirm });
  }

  const [openSections, setOpenSections] = useState({
    company: false, booking: false, invoicing: false, payments: false, policies: false,
  });
  const [editMode, setEditMode] = useState({
    company: false, booking: false, invoicing: false, payments: false, policies: false,
  });

  function toggleSection(key) {
    setOpenSections(prev => ({ ...prev, [key]: !prev[key] }));
  }

  function startEdit(key) {
    // Ensure local draft is in sync with saved before editing
    setS(companySettings);
    setEditMode(prev => ({ ...prev, [key]: true }));
  }

  function saveSection(fields, label, key) {
    ask(`Save ${label} settings?`, () => {
      const patch = {};
      fields.forEach(f => { patch[f] = s[f]; });
      setCompanySettings(prev => ({ ...prev, ...patch }));
      setEditMode(prev => ({ ...prev, [key]: false }));
      showToast(`✓ ${label} saved`);
    });
  }

  function cancelSection(fields, key) {
    ask('Discard unsaved changes?', () => {
      setS(prev => {
        const reset = { ...prev };
        fields.forEach(f => { reset[f] = companySettings[f]; });
        return reset;
      });
      setEditMode(prev => ({ ...prev, [key]: false }));
    });
  }

  const editing = (key) => editMode[key];

  // Helpers so field rows stay clean
  const inp = (field, props = {}) => (
    <input className="fi" value={s[field] || ''} onChange={e => setS(p => ({ ...p, [field]: e.target.value }))} {...props} />
  );
  const sel = (field, options) => (
    <select className="fsel2" value={s[field] || options[0].val} onChange={e => setS(p => ({ ...p, [field]: e.target.value }))}>
      {options.map(o => <option key={o.val} value={o.val}>{o.label}</option>)}
    </select>
  );

  // ── Section field lists (for save/cancel) ────────────────────────────
  const COMPANY_FIELDS   = ['name','phone','email','address','vatNum','currency','vatRate','vatMode'];
  const BOOKING_FIELDS   = ['slug','brandColor','bookingConfirmation'];
  const INV_FIELDS       = ['invPrefix','invNext','invPaymentTerms','invFooter','logoDataUrl','regNum','website','bankName','iban','swift','bankAccountName','bankDetails','invTerms'];
  const PAYMENT_FIELDS   = ['stripePublicKey','stripeSecretKey','defaultDeposit'];
  const POLICY_FIELDS    = ['cancelPolicy','latePolicy','minAge',
    'cancellationPolicy','noShowDelayHours','dateChangeCutoffHours','maxDateChanges','transactionFeePolicy'];

  return (
    <div style={{ maxWidth: 780 }}>

      {/* Custom confirm dialog */}
      {dialog && (
        <ConfirmDialog
          message={dialog.message}
          onConfirm={() => { dialog.onConfirm(); setDialog(null); }}
          onCancel={() => setDialog(null)}
        />
      )}

      {/* ── Company Information ── */}
      <CollapSection
        isOpen={openSections.company} onToggle={() => toggleSection('company')}
        title="🏢 Company Information"
        isEditing={editing('company')}
        onEdit={() => startEdit('company')}
        onSave={() => saveSection(COMPANY_FIELDS, 'Company Information', 'company')}
        onCancel={() => cancelSection(COMPANY_FIELDS, 'company')}
      >
        <Field label="Company name">
          {editing('company') ? inp('name') : <DisplayVal value={s.name} />}
        </Field>
        <Field label="Phone">
          {editing('company') ? inp('phone') : <DisplayVal value={s.phone} />}
        </Field>
        <Field label="Email">
          {editing('company') ? inp('email', { type: 'email' }) : <DisplayVal value={s.email} />}
        </Field>
        <Field label="Address">
          {editing('company') ? inp('address') : <DisplayVal value={s.address} />}
        </Field>
        <Field label="VAT Number">
          {editing('company') ? inp('vatNum') : <DisplayVal value={s.vatNum} />}
        </Field>
        <Field label="Currency">
          {editing('company')
            ? sel('currency', [
                { val: 'EUR', label: 'EUR — Euro (€)' },
                { val: 'GBP', label: 'GBP — British Pound (£)' },
                { val: 'USD', label: 'USD — US Dollar ($)' },
              ])
            : <DisplayVal value={s.currency || 'EUR'} />}
        </Field>
        <Field label="VAT Rate">
          {editing('company')
            ? <input className="fi" type="number" value={s.vatRate || 19} min={0} max={30}
                onChange={e => setS(p => ({ ...p, vatRate: parseFloat(e.target.value) }))} style={{ width: 80 }} />
            : <DisplayVal value={s.vatRate ? `${s.vatRate}%` : '19%'} />}
        </Field>
        <Field label="VAT Mode">
          {editing('company')
            ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {[
                  { val: 'inclusive', label: 'Inclusive', desc: 'Price shown = final price — VAT already inside' },
                  { val: 'exclusive', label: 'Exclusive', desc: 'VAT added on top at checkout — e.g. €50 + 19% = €59.50' },
                ].map(opt => (
                  <label key={opt.val} style={{ display: 'flex', alignItems: 'flex-start', gap: 8, cursor: 'pointer' }}>
                    <input type="radio" name="vatMode" value={opt.val}
                      checked={(s.vatMode || 'inclusive') === opt.val}
                      onChange={() => setS(p => ({ ...p, vatMode: opt.val }))}
                      style={{ marginTop: 2 }} />
                    <div>
                      <div style={{ fontSize: 11, fontWeight: 600 }}>{opt.label}</div>
                      <div style={{ fontSize: 10, color: 'var(--tx3)' }}>{opt.desc}</div>
                    </div>
                  </label>
                ))}
              </div>
            )
            : <DisplayVal value={s.vatMode === 'exclusive' ? 'Exclusive (VAT added at checkout)' : 'Inclusive (VAT inside price)'} />}
        </Field>
      </CollapSection>

      {/* ── Booking Page ── */}
      <CollapSection
        isOpen={openSections.booking} onToggle={() => toggleSection('booking')}
        title="🌐 Booking Page"
        isEditing={editing('booking')}
        onEdit={() => startEdit('booking')}
        onSave={() => saveSection(BOOKING_FIELDS, 'Booking Page', 'booking')}
        onCancel={() => cancelSection(BOOKING_FIELDS, 'booking')}
      >
        <Field label="URL Slug">
          {editing('booking')
            ? <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <span style={{ fontSize: 11, color: 'var(--tx3)' }}>fleetrent.app/book/</span>
                <input className="fi" value={s.slug || ''} onChange={e => setS(p => ({ ...p, slug: e.target.value }))} style={{ flex: 1 }} />
              </div>
            : <DisplayVal value={s.slug ? `fleetrent.app/book/${s.slug}` : undefined} placeholder="Not set" />}
        </Field>
        <Field label="Brand Color">
          {editing('booking')
            ? <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <input type="color" value={s.brandColor || '#1A3A5C'} onChange={e => setS(p => ({ ...p, brandColor: e.target.value }))}
                  style={{ width: 40, height: 32, borderRadius: 5, border: '1px solid var(--bd)', cursor: 'pointer' }} />
                <span style={{ fontSize: 11, color: 'var(--tx3)', fontFamily: "'DM Mono',monospace" }}>{s.brandColor || '#1A3A5C'}</span>
              </div>
            : <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <div style={{ width: 20, height: 20, borderRadius: 4, background: s.brandColor || '#1A3A5C', border: '1px solid var(--bd)' }} />
                <span style={{ fontSize: 12, fontFamily: "'DM Mono',monospace" }}>{s.brandColor || '#1A3A5C'}</span>
              </div>}
        </Field>
        <Field label="Confirmation">
          {editing('booking')
            ? sel('bookingConfirmation', [
                { val: 'manual', label: 'Manual — you review each booking' },
                { val: 'auto',   label: 'Auto-confirm if vehicle available' },
              ])
            : <DisplayVal value={s.bookingConfirmation === 'auto' ? 'Auto-confirm' : 'Manual review'} />}
        </Field>
      </CollapSection>

      {/* ── Invoicing & PDF ── */}
      <CollapSection
        isOpen={openSections.invoicing} onToggle={() => toggleSection('invoicing')}
        title="🧾 Pro Forma & PDF Settings"
        isEditing={editing('invoicing')}
        onEdit={() => startEdit('invoicing')}
        onSave={() => saveSection(INV_FIELDS, 'Pro Forma & PDF', 'invoicing')}
        onCancel={() => cancelSection(INV_FIELDS, 'invoicing')}
      >
        <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--tx3)', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 4 }}>General</div>
        <Field label="Pro forma prefix">
          {editing('invoicing')
            ? <input className="fi" value={s.invPrefix || 'PF-'} maxLength={8}
                onChange={e => setS(p => ({ ...p, invPrefix: e.target.value }))} style={{ width: 100 }} />
            : <DisplayVal value={s.invPrefix || 'PF-'} />}
        </Field>
        <Field label="Next pro forma #">
          {editing('invoicing')
            ? <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <input className="fi" type="number" value={s.invNext || 1} min={1}
                  onChange={e => setS(p => ({ ...p, invNext: parseInt(e.target.value) || 1 }))} style={{ width: 80 }} />
                <span style={{ fontSize: 11, color: 'var(--tx3)', fontFamily: "'DM Mono',monospace" }}>
                  → {(s.invPrefix || 'PF-') + String(s.invNext || 1).padStart(4, '0')}
                </span>
              </div>
            : <DisplayVal value={(s.invPrefix || 'PF-') + String(s.invNext || 1).padStart(4, '0')} />}
        </Field>
        <Field label="Payment terms">
          {editing('invoicing')
            ? <input className="fi" value={s.invPaymentTerms || ''} placeholder="e.g. Payment due within 30 days"
                onChange={e => setS(p => ({ ...p, invPaymentTerms: e.target.value }))} />
            : <DisplayVal value={s.invPaymentTerms} placeholder="Not set" />}
        </Field>
        <Field label="Pro forma footer">
          {editing('invoicing')
            ? <input className="fi" value={s.invFooter || ''} placeholder="e.g. Thank you for your business!"
                onChange={e => setS(p => ({ ...p, invFooter: e.target.value }))} />
            : <DisplayVal value={s.invFooter} placeholder="Not set" />}
        </Field>

        <SubHeader label="Company Identity (PDF Header)" />
        <Field label="Company logo">
          {editing('invoicing')
            ? <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                {s.logoDataUrl && <img src={s.logoDataUrl} alt="logo" style={{ height: 48, maxWidth: 120, objectFit: 'contain', border: '1px solid var(--bd)', borderRadius: 6, padding: 4, background: '#fff' }} />}
                <div>
                  <input type="file" accept="image/*" style={{ fontSize: 11 }}
                    onChange={e => {
                      const file = e.target.files[0]; if (!file) return;
                      const reader = new FileReader();
                      reader.onload = ev => setS(p => ({ ...p, logoDataUrl: ev.target.result }));
                      reader.readAsDataURL(file);
                    }} />
                  {s.logoDataUrl && <button className="btn btn-ghost btn-sm" style={{ marginTop: 4 }} onClick={() => setS(p => ({ ...p, logoDataUrl: null }))}>Remove</button>}
                  <div style={{ fontSize: 10, color: 'var(--tx3)', marginTop: 3 }}>PNG or JPG · max 500KB</div>
                </div>
              </div>
            : s.logoDataUrl
              ? <img src={s.logoDataUrl} alt="logo" style={{ height: 36, maxWidth: 100, objectFit: 'contain', border: '1px solid var(--bd)', borderRadius: 4, padding: 2, background: '#fff' }} />
              : <DisplayVal placeholder="No logo uploaded" />}
        </Field>
        <Field label="Reg. number">
          {editing('invoicing')
            ? <input className="fi" value={s.regNum || ''} placeholder="e.g. HE 123456" onChange={e => setS(p => ({ ...p, regNum: e.target.value }))} />
            : <DisplayVal value={s.regNum} placeholder="Not set" />}
        </Field>
        <Field label="Website">
          {editing('invoicing')
            ? <input className="fi" value={s.website || ''} placeholder="www.yourcompany.com" onChange={e => setS(p => ({ ...p, website: e.target.value }))} />
            : <DisplayVal value={s.website} placeholder="Not set" />}
        </Field>

        <SubHeader label="Bank & Payment Details (PDF Footer)" />
        <Field label="Bank name">
          {editing('invoicing')
            ? <input className="fi" value={s.bankName || ''} placeholder="e.g. Bank of Cyprus" onChange={e => setS(p => ({ ...p, bankName: e.target.value }))} />
            : <DisplayVal value={s.bankName} placeholder="Not set" />}
        </Field>
        <Field label="IBAN">
          {editing('invoicing')
            ? <input className="fi" value={s.iban || ''} placeholder="e.g. CY39 0020 0195 0000 3570 4462 9424"
                onChange={e => setS(p => ({ ...p, iban: e.target.value }))} style={{ fontFamily: "'DM Mono',monospace", fontSize: 12 }} />
            : <DisplayVal value={s.iban} placeholder="Not set" />}
        </Field>
        <Field label="SWIFT / BIC">
          {editing('invoicing')
            ? <input className="fi" value={s.swift || ''} placeholder="e.g. BCYPCY2N"
                onChange={e => setS(p => ({ ...p, swift: e.target.value }))} style={{ fontFamily: "'DM Mono',monospace", fontSize: 12 }} />
            : <DisplayVal value={s.swift} placeholder="Not set" />}
        </Field>
        <Field label="Account holder">
          {editing('invoicing')
            ? <input className="fi" value={s.bankAccountName || ''} placeholder="e.g. D.LIANEZIDES LTD" onChange={e => setS(p => ({ ...p, bankAccountName: e.target.value }))} />
            : <DisplayVal value={s.bankAccountName} placeholder="Not set" />}
        </Field>
        <Field label="Bank details note">
          {editing('invoicing')
            ? <textarea className="fi" rows={2} style={{ resize: 'vertical', fontSize: 12 }} value={s.bankDetails || ''}
                placeholder="e.g. All bank fees are responsibility of the buyer."
                onChange={e => setS(p => ({ ...p, bankDetails: e.target.value }))} />
            : <DisplayVal value={s.bankDetails} placeholder="Not set" />}
        </Field>
        <Field label="Terms & conditions">
          {editing('invoicing')
            ? <textarea className="fi" rows={3} style={{ resize: 'vertical', fontSize: 12 }} value={s.invTerms || ''}
                placeholder="Additional terms shown at the bottom of the invoice PDF..."
                onChange={e => setS(p => ({ ...p, invTerms: e.target.value }))} />
            : <DisplayVal value={s.invTerms} placeholder="Not set" />}
        </Field>
      </CollapSection>

      {/* ── Payments ── */}
      <CollapSection
        isOpen={openSections.payments} onToggle={() => toggleSection('payments')}
        title="💳 Payments"
        isEditing={editing('payments')}
        onEdit={() => startEdit('payments')}
        onSave={() => saveSection(PAYMENT_FIELDS, 'Payments', 'payments')}
        onCancel={() => cancelSection(PAYMENT_FIELDS, 'payments')}
      >
        <Field label="Stripe Public Key">
          {editing('payments')
            ? <input className="fi" value={s.stripePublicKey || ''} placeholder="pk_live_..." onChange={e => setS(p => ({ ...p, stripePublicKey: e.target.value }))} />
            : <DisplayVal value={s.stripePublicKey ? s.stripePublicKey.slice(0, 12) + '••••••••' : undefined} placeholder="Not set" />}
        </Field>
        <Field label="Stripe Secret Key">
          {editing('payments')
            ? <input className="fi" type="password" value={s.stripeSecretKey || ''} placeholder="sk_live_..." onChange={e => setS(p => ({ ...p, stripeSecretKey: e.target.value }))} />
            : <DisplayVal value={s.stripeSecretKey ? '••••••••••••' : undefined} placeholder="Not set" />}
        </Field>
        <Field label="Default Deposit">
          {editing('payments')
            ? <input className="fi" type="number" value={s.defaultDeposit || ''} style={{ width: 100 }} onChange={e => setS(p => ({ ...p, defaultDeposit: parseFloat(e.target.value) }))} />
            : <DisplayVal value={s.defaultDeposit ? `€${s.defaultDeposit}` : undefined} placeholder="Not set" />}
        </Field>
      </CollapSection>

      {/* ── Policies ── */}
      <CollapSection
        isOpen={openSections.policies} onToggle={() => toggleSection('policies')}
        title="📋 Policies"
        isEditing={editing('policies')}
        onEdit={() => startEdit('policies')}
        onSave={() => saveSection(POLICY_FIELDS, 'Policies', 'policies')}
        onCancel={() => cancelSection(POLICY_FIELDS, 'policies')}
      >
        {/* ── Cancellation refund tiers ───────────────────────────── */}
        <SubHeader label="Cancellation Refund Tiers" />
        <div style={{ fontSize: 11, color: 'var(--tx3)', marginBottom: 8 }}>
          When a customer self-cancels, the system matches the highest tier they still qualify for and refunds that percentage. Sorted by hours-before-pickup, descending.
        </div>
        {(s.cancellationPolicy || []).map((tier, i) => {
          const hours = parseInt(tier.hoursBeforePickup) || 0;
          const labelHours = hours >= 24 ? `${Math.floor(hours / 24)} day${Math.floor(hours / 24) === 1 ? '' : 's'}${hours % 24 ? ` ${hours % 24}h` : ''}` : `${hours} hours`;
          return (
            <div key={i} style={{ display: 'flex', gap: 8, alignItems: 'center', padding: '6px 8px', marginBottom: 4, background: 'var(--sur2)', border: '1px solid var(--bd)', borderRadius: 6 }}>
              <span style={{ fontSize: 11, color: 'var(--tx3)', minWidth: 110 }}>If cancelled at least</span>
              {editing('policies') ? (
                <input type="number" className="fi" style={{ width: 90 }} min="0" value={tier.hoursBeforePickup}
                  onChange={e => setS(p => ({ ...p, cancellationPolicy: p.cancellationPolicy.map((t, idx) => idx === i ? { ...t, hoursBeforePickup: parseInt(e.target.value) || 0 } : t) }))} />
              ) : <span style={{ fontWeight: 600, fontFamily: "'DM Mono',monospace", minWidth: 90 }}>{tier.hoursBeforePickup}h</span>}
              <span style={{ fontSize: 11, color: 'var(--tx3)' }}>hours before pickup → refund</span>
              {editing('policies') ? (
                <input type="number" className="fi" style={{ width: 70 }} min="0" max="100" value={tier.refundPercent}
                  onChange={e => setS(p => ({ ...p, cancellationPolicy: p.cancellationPolicy.map((t, idx) => idx === i ? { ...t, refundPercent: Math.min(100, Math.max(0, parseFloat(e.target.value) || 0)) } : t) }))} />
              ) : <span style={{ fontWeight: 700, color: tier.refundPercent === 100 ? 'var(--gr)' : tier.refundPercent === 0 ? 'var(--ac)' : 'var(--am)', minWidth: 50 }}>{tier.refundPercent}%</span>}
              <span style={{ fontSize: 10, color: 'var(--tx3)', flex: 1 }}>= {labelHours}+</span>
              {editing('policies') && (
                <button type="button" className="btn btn-ghost btn-sm" style={{ color: 'var(--ac)', padding: '2px 8px' }}
                  onClick={() => setS(p => ({ ...p, cancellationPolicy: p.cancellationPolicy.filter((_, idx) => idx !== i) }))}>✕</button>
              )}
            </div>
          );
        })}
        {editing('policies') && (
          <button type="button" className="btn btn-ghost btn-sm" style={{ marginTop: 4 }}
            onClick={() => setS(p => ({ ...p, cancellationPolicy: [...(p.cancellationPolicy || []), { hoursBeforePickup: 0, refundPercent: 0 }].sort((a, b) => b.hoursBeforePickup - a.hoursBeforePickup) }))}>
            + Add tier
          </button>
        )}

        {/* ── Change & no-show rules ──────────────────────────────── */}
        <SubHeader label="Change & No-Show Rules" />
        <Field label="Lock changes within">
          {editing('policies')
            ? <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <input type="number" className="fi" style={{ width: 80 }} min="0" value={s.dateChangeCutoffHours ?? 24}
                  onChange={e => setS(p => ({ ...p, dateChangeCutoffHours: parseInt(e.target.value) || 0 }))} />
                <span style={{ fontSize: 11, color: 'var(--tx3)' }}>hours of pickup</span>
              </span>
            : <DisplayVal value={`${s.dateChangeCutoffHours ?? 24} hours before pickup`} />}
        </Field>
        <Field label="Max date changes">
          {editing('policies')
            ? <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <input type="number" className="fi" style={{ width: 80 }} min="0" max="10" value={s.maxDateChanges ?? 1}
                  onChange={e => setS(p => ({ ...p, maxDateChanges: parseInt(e.target.value) || 0 }))} />
                <span style={{ fontSize: 11, color: 'var(--tx3) ' }}>per booking before customer must call</span>
              </span>
            : <DisplayVal value={`${s.maxDateChanges ?? 1} change${(s.maxDateChanges ?? 1) === 1 ? '' : 's'} per booking`} />}
        </Field>
        <Field label="No-show auto-cancel">
          {editing('policies')
            ? <select className="fsel2" value={s.noShowDelayHours ?? 24} onChange={e => setS(p => ({ ...p, noShowDelayHours: parseInt(e.target.value) }))}>
                <option value={0}>Off — operator cancels manually</option>
                <option value={12}>12 hours after pickup time</option>
                <option value={24}>24 hours after pickup time</option>
                <option value={48}>48 hours after pickup time</option>
              </select>
            : <DisplayVal value={
                (s.noShowDelayHours ?? 24) === 0 ? 'Off — operator cancels manually'
                : `${s.noShowDelayHours} hours after pickup time`
              } />}
        </Field>

        {/* ── Refund transaction fees ─────────────────────────────── */}
        <SubHeader label="Refund Transaction Fees" />
        <div style={{ fontSize: 11, color: 'var(--tx3)', marginBottom: 8 }}>
          Payment processors (Stripe, Viva, PayPal) keep their fee even when you refund. You can choose to absorb it (customer gets the full amount) or deduct it from the refund.
        </div>
        {[
          { key: 'onFullCancel',    label: 'Deduct fee on full cancellations',           help: 'Most operators absorb (off)' },
          { key: 'onPartialRefund', label: 'Deduct fee on partial refunds (date change)', help: 'Most operators deduct (on)' },
          { key: 'onLateOrNoShow',  label: 'Deduct fee on late cancel / no-show refunds', help: 'Most operators deduct (on)' },
        ].map(opt => {
          const fp = s.transactionFeePolicy || {};
          const checked = !!fp[opt.key];
          return (
            <div key={opt.key} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '6px 8px', marginBottom: 4, background: 'var(--sur2)', border: '1px solid var(--bd)', borderRadius: 6 }}>
              <input type="checkbox" disabled={!editing('policies')} checked={checked}
                onChange={e => setS(p => ({ ...p, transactionFeePolicy: { ...(p.transactionFeePolicy || {}), [opt.key]: e.target.checked } }))} />
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 11, fontWeight: 600 }}>{opt.label}</div>
                <div style={{ fontSize: 9, color: 'var(--tx3)' }}>{opt.help}</div>
              </div>
            </div>
          );
        })}
        <Field label="Fee percentage">
          {editing('policies')
            ? <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <input type="number" className="fi" style={{ width: 80 }} min="0" max="10" step="0.1"
                  value={s.transactionFeePolicy?.feePercent ?? 3.0}
                  onChange={e => setS(p => ({ ...p, transactionFeePolicy: { ...(p.transactionFeePolicy || {}), feePercent: parseFloat(e.target.value) || 0 } }))} />
                <span style={{ fontSize: 11, color: 'var(--tx3)' }}>% of refund amount · typical: Stripe 1.4–2.9% · Viva 1.95% · PayPal 2.9%</span>
              </span>
            : <DisplayVal value={`${s.transactionFeePolicy?.feePercent ?? 3.0}%`} />}
        </Field>

        {/* ── Legacy free-text policies ───────────────────────────── */}
        <SubHeader label="Other Policies (free text)" />
        <Field label="Cancellation policy text">
          {editing('policies')
            ? <textarea className="fi" value={s.cancelPolicy || ''} rows={3} style={{ resize: 'vertical' }}
                placeholder="Plain-English summary shown to the customer (e.g. on emails or T&Cs)"
                onChange={e => setS(p => ({ ...p, cancelPolicy: e.target.value }))} />
            : <DisplayVal value={s.cancelPolicy} placeholder="Not set" />}
        </Field>
        <Field label="Late return fee">
          {editing('policies')
            ? <input className="fi" value={s.latePolicy || ''} placeholder="e.g. 1.5× daily rate" onChange={e => setS(p => ({ ...p, latePolicy: e.target.value }))} />
            : <DisplayVal value={s.latePolicy} placeholder="Not set" />}
        </Field>
        <Field label="Min rental age">
          {editing('policies')
            ? <input className="fi" type="number" value={s.minAge || 21} style={{ width: 80 }} onChange={e => setS(p => ({ ...p, minAge: parseInt(e.target.value) }))} />
            : <DisplayVal value={s.minAge ? `${s.minAge} years` : '21 years'} />}
        </Field>
      </CollapSection>

      {/* ── Danger Zone ── */}
      <div style={{
        marginTop: 18, border: '1.5px solid #fca5a5', borderRadius: 'var(--rl)',
        background: '#fef2f2', padding: '14px 18px',
      }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: '#b91c1c', marginBottom: 4 }}>⚠️ Danger Zone</div>
        <div style={{ fontSize: 11, color: '#7f1d1d', marginBottom: 12 }}>
          Irreversible actions. Use only when you understand the consequences.
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '10px 12px', background: '#fff', border: '1px solid #fecaca', borderRadius: 'var(--r)' }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 12, fontWeight: 600, color: '#7f1d1d' }}>Reset to default demo data</div>
            <div style={{ fontSize: 10, color: '#9f1239', marginTop: 2 }}>
              Wipes all locally stored data (rentals, customers, invoices, templates, settings) and re-seeds the app with the default demo dataset. Use this if your test data has drifted into an inconsistent state.
            </div>
          </div>
          <button
            type="button"
            className="btn btn-primary"
            style={{ background: '#dc2626', borderColor: '#b91c1c', whiteSpace: 'nowrap' }}
            onClick={() => {
              if (!window.confirm('Reset ALL data?\n\nEverything in this browser (rentals, customers, invoices, agreement templates, company settings) will be deleted and replaced with the demo defaults.\n\nThis cannot be undone. Continue?')) return;
              resetDemoData && resetDemoData();
            }}
          >
            🗑 Reset all data
          </button>
        </div>
      </div>

    </div>
  );
}
